/**
 * Transform Drive folders into course objects for display in the directory
 */

/**
 * Transform a Drive folder into a course object
 * @param {Object} folder - Drive folder object from fetchTeachLeagueFolders
 * @returns {Object} - Course object compatible with existing course structure
 */
export function driveFolderToCourse(folder) {
  const {
    folderId,
    folderName,
    folderLink,
    ownerEmail,
    ownerName,
    isOwner,
    fileCount = 0,
    modifiedTime,
  } = folder;

  // Generate a unique ID for the Drive course
  const courseId = `drive-${folderId}`;

  // Create a default image (you can customize this based on contributor or content)
  const defaultImage = 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=400&h=250&fit=crop';

  return {
    id: courseId,
    name: folderName || 'Shared Folder',
    icon: '📁',
    image: defaultImage,
    credit: ['Community Shared'], // Tag for filtering
    intro: ownerEmail ? `Shared by ${ownerName || ownerEmail}` : 'Community shared teaching materials',

    // Drive-specific metadata
    source: 'drive',
    driveData: {
      folderId,
      folderLink,
      ownerEmail,
      ownerName,
      isOwner,
      fileCount,
      modifiedTime,
    },

    // Empty fields for compatibility with CourseDetail
    description: null,
    essentialQuestions: [],
    youtube: null,
    warmups: null,
    extra: null,
    curriculum: null,
  };
}

/**
 * Transform multiple Drive folders into course objects
 * @param {Array} folders - Array of Drive folder objects
 * @returns {Array} - Array of course objects
 */
export function driveFoldersToCourses(folders) {
  if (!Array.isArray(folders)) {
    return [];
  }

  return folders.map(driveFolderToCourse);
}

/**
 * Get contributor info from a Drive course
 * @param {Object} course - Course object
 * @returns {Object|null} - Contributor info or null
 */
export function getDriveCourseContributor(course) {
  if (course?.source !== 'drive' || !course?.driveData) {
    return null;
  }

  return {
    name: course.driveData.ownerName,
    email: course.driveData.ownerEmail,
    isOwner: course.driveData.isOwner,
  };
}

/**
 * Check if a course is from Drive
 * @param {Object} course - Course object
 * @returns {boolean}
 */
export function isDriveCourse(course) {
  return course?.source === 'drive' && course?.driveData?.folderId;
}
